import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { AnimatePresence, motion } from 'framer-motion';
import ModeSelector, { openFilePicker } from './ModeSelector';
import CaptureReview from './CaptureReview';
import DecorativeLabel from './DecorativeLabel';
import ExtractionProgress from './ExtractionProgress';
import RegisterDraft from './RegisterDraft';
import DuplicateAlert from './DuplicateAlert';
import SessionHeader from './SessionHeader';
import DiscardConfirmation from './DiscardConfirmation';
import ScanSuccessScreen from './ScanSuccessScreen';
import { useCommitFlow } from '@/hooks/useCommitFlow';
import { useScanSession } from '@/hooks/useScanSession';
import { captureError } from '@/utils/captureError';
import type { Wine, ScanStage, WineDraft, ExtractionResult, DuplicateCandidate, ExtractionErrorCode, WineBriefContext } from '@/types';
import { compressImageForExtraction, createPreviewUrl } from '@/utils/imageCompression';
import { extractWineFromLabel, ExtractionError } from '@/services/extractionService';
import { analyseImageQuality } from '@/utils/imageQuality';
import { findDuplicates } from '@/services/duplicateService';
import { inventoryService } from '@/services/inventoryService';
import { showToast } from '@/components/rc';
import UpgradePrompt from '@/components/UpgradePrompt';
import { useProfile } from '@/context/ProfileContext';
import { CONFIG } from '@/constants';
import { confirmProdWrite } from '@/components/ProdWriteGuard';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useKeyboardVisible } from '@/hooks/useKeyboardVisible';
import { useIsMobile } from '@/components/ui/use-mobile';
import { trackEvent } from '@/config/analytics';
import { useScanReducer } from '@/hooks/useScanReducer';
import type { ScanAction } from '@/hooks/useScanReducer';

interface ScanRegisterOverlayProps {
  open: boolean;
  onClose: () => void;
  inventory: Wine[];
  onWineCommitted?: (docId: string | string[]) => void;
  onViewWine?: (wine: Wine) => void;
  prefillData?: Partial<Wine> | null;
  onAskRemy?: (draft: WineDraft) => void;
  manualEntryDirect?: boolean;
  onClearManualEntryDirect?: () => void;
  onImport?: () => void;
}

const ScanRegisterOverlay: React.FC<ScanRegisterOverlayProps> = ({
  open, onClose, inventory, onWineCommitted, onViewWine,
  prefillData, onAskRemy, manualEntryDirect, onClearManualEntryDirect, onImport,
}) => {
  const { isPremium, profile } = useProfile();
  const [upgradePromptOpen, setUpgradePromptOpen] = useState(false);
  const isMobile = useIsMobile();
  const { keyboardVisible } = useKeyboardVisible();
  const reducedMotion = useReducedMotion();
  const stageMotion = reducedMotion
    ? { initial: {}, animate: {}, exit: {}, transition: { duration: 0 } }
    : { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -16 }, transition: { duration: 0.2 } };

  const [state, dispatch] = useScanReducer();
  const [duplicateCandidate, setDuplicateCandidate] = useState<DuplicateCandidate | null>(null);
  const [moreFieldsExpanded, setMoreFieldsExpanded] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const prevOpenRef = useRef(open);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const session = useScanSession();

  // ── Open/close sync ──
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      if (manualEntryDirect) {
        dispatch({ type: 'MANUAL_ENTRY' });
        setMoreFieldsExpanded(true);
        onClearManualEntryDirect?.();
      } else if (prefillData) {
        dispatch({ type: 'PREFILL_OPEN', fields: prefillData });
        setMoreFieldsExpanded(true);
      } else {
        dispatch({ type: 'OPEN' });
        setMoreFieldsExpanded(false);
      }
      setDuplicateCandidate(null);
    } else if (!open && prevOpenRef.current) {
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
      dispatch({ type: 'CLOSE' });
      setDuplicateCandidate(null);
    }
    prevOpenRef.current = open;
  }, [open, prefillData, manualEntryDirect, onClearManualEntryDirect]);

  // ── Browser back button ──
  // Stage/session are read through refs so this effect depends on `open` alone.
  // Re-running it on every stage change pushed a fresh history entry each time —
  // including one microseconds after "SCAN ANOTHER" opens the camera, which made
  // mobile WebViews drop the pending file-chooser result (bottle never scanned).
  const stageRef = useRef(state.stage);
  stageRef.current = state.stage;
  const sessionActiveRef = useRef(session.isActive);
  sessionActiveRef.current = session.isActive;

  useEffect(() => {
    if (!open) return;
    const handlePopState = () => {
      const stage = stageRef.current;
      if (sessionActiveRef.current && (stage === 'draft' || stage === 'extracting' || stage === 'reviewing' || stage === 'decorative')) {
        setShowDiscard(true);
        window.history.pushState({ scanOverlay: true }, '');
      } else {
        onCloseRef.current();
      }
    };
    window.history.pushState({ scanOverlay: true }, '');
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [open]);

  // ── Extraction ──
  const runExtraction = useCallback(async (file: File) => {
    if (!navigator.onLine) {
      dispatch({ type: 'EXTRACTION_FAIL', error: 'No internet connection. Try entering details manually.', errorCode: 'PROXY_ERROR' });
      return;
    }
    try {
      const base64 = await compressImageForExtraction(file);
      const { fields, extraction } = await extractWineFromLabel(base64);
      const filledCount = Object.values(extraction.fields).filter(f => f.value != null).length;
      const isDecorative = extraction.isDecorativeLabel
        || (filledCount <= 2 && (extraction.imageQuality === 'high' || extraction.imageQuality === 'medium'));
      if (isDecorative) {
        trackEvent('scan_decorative_detected');
        dispatch({ type: 'DECORATIVE_DETECTED' });
        return;
      }
      dispatch({ type: 'EXTRACTION_SUCCESS', fields, extraction });
    } catch (e: unknown) {
      const err = e instanceof ExtractionError ? e : null;
      const errorCode = err ? err.code : 'UNKNOWN';
      const message = e instanceof Error ? e.message : 'Failed to read label';
      dispatch({ type: 'EXTRACTION_FAIL', error: message, errorCode: errorCode as ExtractionErrorCode });
    }
  }, []);

  const handleCameraCapture = useCallback((file: File) => {
    trackEvent('scan_started');
    dispatch({ type: 'CAPTURE', file, previewUrl: createPreviewUrl(file) });
  }, []);

  const handleGalleryCapture = useCallback((file: File) => {
    dispatch({ type: 'GALLERY_CAPTURE', file, previewUrl: createPreviewUrl(file) });
    runExtraction(file);
    analyseImageQuality(file).catch(() => {});
  }, [runExtraction]);

  const handleReviewAccept = useCallback(() => {
    if (!state.rawFile) return;
    dispatch({ type: 'REVIEW_ACCEPT' });
    runExtraction(state.rawFile);
  }, [state.rawFile, runExtraction]);

  const handleRetry = useCallback(() => {
    if (!state.rawFile) return;
    dispatch({ type: 'RETRY' });
    runExtraction(state.rawFile);
  }, [state.rawFile, runExtraction]);

  const handleRetake = useCallback(() => {
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    dispatch({ type: 'RETAKE' });
    setDuplicateCandidate(null);
  }, [state.previewUrl]);

  // ── Commit flow (extracted hook) ──
  const { commitStage, commitError, lastCommittedName, handleConfirm, handleCommitAnimationComplete, handleScanAnother, handleDone, handleDismissCommitError } = useCommitFlow({
    draft: state.draft,
    rawFile: state.rawFile,
    previewUrl: state.previewUrl,
    inventory,
    duplicateCandidate,
    setDuplicateCandidate,
    isPremium,
    profileCurrency: profile.currency,
    session,
    dispatch,
    onClose,
    onWineCommitted,
    onCameraCapture: handleCameraCapture,
    setUpgradePromptOpen,
  });

  // ── Remaining handlers ──
  const handleManualEntry = useCallback(() => {
    dispatch({ type: 'MANUAL_ENTRY' });
    setMoreFieldsExpanded(true);
  }, []);

  const handleUpdateFields = useCallback((fields: Partial<Wine>) => {
    dispatch({ type: 'UPDATE_DRAFT', fields });
  }, []);

  const handleAskRemy = useCallback(() => {
    if (state.draft && onAskRemy) onAskRemy(state.draft);
  }, [state.draft, onAskRemy]);

  const handleViewExisting = useCallback(() => {
    if (!duplicateCandidate) return;
    setDuplicateCandidate(null);
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    onClose();
    onViewWine?.(duplicateCandidate.existingWine);
  }, [duplicateCandidate, state.previewUrl, onClose, onViewWine]);

  const handleAddToExisting = useCallback(async () => {
    if (!duplicateCandidate) return;
    const existing = duplicateCandidate.existingWine;
    const newQty = (existing.quantity || 1) + 1;
    const delta = newQty - (Number(existing.quantity) || 0);
    const currentTotal = inventory.reduce((sum, w) => sum + (Number(w.quantity) || 0), 0);
    if (!isPremium && currentTotal + delta > CONFIG.FREE_TIER.MAX_BOTTLES) {
      setUpgradePromptOpen(true);
      return;
    }
    await inventoryService.updateField(existing.id, 'quantity', newQty);
    setDuplicateCandidate(null);
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    onClose();
    showToast({ tone: 'success', message: `${existing.producer} quantity updated to ${newQty}` });
  }, [duplicateCandidate, state.previewUrl, inventory, isPremium, onClose]);

  const handleKeepSeparate = useCallback(() => {
    setDuplicateCandidate(null);
    handleConfirm();
  }, [handleConfirm]);

  const handleClose = useCallback(() => {
    if (session.isActive && state.stage === 'draft') {
      setShowDiscard(true);
      return;
    }
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    setDuplicateCandidate(null);
    onClose();
  }, [onClose, state.previewUrl, session.isActive, state.stage]);

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscard(false);
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    openFilePicker({ capture: true }, handleCameraCapture);
    dispatch({ type: 'SCAN_NEXT' });
  }, [state.previewUrl, handleCameraCapture]);

  const handleDiscardKeep = useCallback(() => setShowDiscard(false), []);

  // ── Image quality warning ──
  const isDraftDecorative = state.draft?.extraction?.isDecorativeLabel === true;
  const imageQualityWarning = isDraftDecorative
    ? 'Decorative label detected. No text found on this side. Try scanning the back label, or fill in the details below.'
    : state.draft?.extraction?.imageQuality === 'low'
      ? 'Image quality is low — some fields may need correction.'
      : null;

  if (!open) return null;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        showClose={false}
        className={`!max-w-full sm:!max-w-3xl w-full overflow-hidden bg-[var(--rc-surface-primary)] border-[var(--rc-divider-emphasis-weight)] border-[var(--rc-ink-primary)] shadow-[var(--rc-shadow-elevated)] p-0 gap-0 ${
          isMobile
            // Full-bleed on phones, so it sits outside Layout's safe-area
            // padding and must inset itself — without this the "Add Wine"
            // title renders hard against (and on notched devices under) the
            // status bar.
            ? '!inset-0 !translate-x-0 !translate-y-0 !rounded-none !w-full !h-full pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]'
            : 'h-[90dvh] max-h-[90dvh] rounded-[var(--rc-radius-lg)]'
        }`}
      >
        <DialogTitle className="sr-only">Add Wine</DialogTitle>

        {session.isActive && (
          <SessionHeader bottleNumber={session.bottleCount} onDone={handleDone} />
        )}

        <AnimatePresence mode="wait">
          {state.stage === 'mode-select' && (
            <motion.div key="mode-select" {...stageMotion}>
              <ModeSelector
                onCameraCapture={handleCameraCapture}
                onGalleryCapture={handleGalleryCapture}
                onManualEntry={handleManualEntry}
                onImport={onImport}
              />
            </motion.div>
          )}

          {state.stage === 'reviewing' && state.rawFile && state.previewUrl && (
            <motion.div key="reviewing" {...stageMotion}>
              <CaptureReview
                file={state.rawFile}
                previewUrl={state.previewUrl}
                onAccept={handleReviewAccept}
                onRetake={handleRetake}
              />
            </motion.div>
          )}

          {state.stage === 'decorative' && state.previewUrl && (
            <motion.div key="decorative" {...stageMotion}>
              <DecorativeLabel
                previewUrl={state.previewUrl}
                onScanBack={handleRetake}
                onManualEntry={handleManualEntry}
              />
            </motion.div>
          )}

          {state.stage === 'extracting' && (
            <motion.div key="extracting" {...stageMotion}>
              <ExtractionProgress
                previewUrl={state.previewUrl}
                error={state.error}
                errorCode={state.errorCode}
                isOffline={!navigator.onLine}
                onRetake={handleRetake}
                onManualEntry={handleManualEntry}
                onRetry={handleRetry}
              />
            </motion.div>
          )}

          {(state.stage === 'draft' || state.stage === 'committing' || state.stage === 'committed') && state.draft && (
            <motion.div key="draft" {...stageMotion}>
              <RegisterDraft
                draft={state.draft}
                onUpdateFields={handleUpdateFields}
                onConfirm={handleConfirm}
                onRetake={handleRetake}
                isCommitting={state.stage === 'committing'}
                commitStage={commitStage}
                commitError={commitError}
                onRetryCommit={handleConfirm}
                onDismissCommitError={handleDismissCommitError}
                onCommitAnimationComplete={handleCommitAnimationComplete}
                onAskRemy={onAskRemy ? handleAskRemy : undefined}
                imageQualityWarning={imageQualityWarning}
                moreFieldsExpanded={moreFieldsExpanded}
                onToggleMoreFields={() => setMoreFieldsExpanded(prev => !prev)}
                isMobile={isMobile}
                keyboardVisible={keyboardVisible}
              />
            </motion.div>
          )}

          {state.stage === 'success-screen' && (
            <motion.div key="success-screen" {...stageMotion}>
              <ScanSuccessScreen
                wineName={lastCommittedName.current}
                onScanAnother={handleScanAnother}
                onDone={handleDone}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {duplicateCandidate && (
          <DuplicateAlert
            candidate={duplicateCandidate}
            onAddToExisting={handleAddToExisting}
            onKeepSeparate={handleKeepSeparate}
            onViewExisting={handleViewExisting}
          />
        )}

        {showDiscard && (
          <DiscardConfirmation
            title={state.stage === 'draft' ? 'DISCARD THIS SCAN?' : 'Finish session?'}
            message={state.stage === 'draft' ? 'Your current scan will be lost.' : `${session.bottleCount} bottles have been added.`}
            onDiscard={handleDiscardConfirm}
            onKeep={handleDiscardKeep}
          />
        )}

        {upgradePromptOpen && (
          <UpgradePrompt variant="modal" feature="bottles" onDismiss={() => setUpgradePromptOpen(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScanRegisterOverlay;
